import { useMemo, useState } from 'react';
import { NavLink, useLocation } from 'react-router';
import { useQuery } from '@tanstack/react-query';
import { ChevronDown, LogOut, PanelLeftClose, PanelLeftOpen, X } from 'lucide-react';
import { api } from '@/shared/api/cliente';
import type { GrupoDeMenu, ItemDeMenu, Menu } from '@/shared/api/tipos';
import { useSesion } from '@/shared/sesion/SesionProvider';
import { Icono } from '@/shared/ui/Icono';

const CLAVE_GRUPO_ABIERTO = 'civitas.menu.grupo';

interface Props {

  abierta: boolean;

  contraida: boolean;
  esEscritorio: boolean;
  alCerrar: () => void;
  alAlternarContraida: () => void;
  alNavegar: () => void;
}

export function BarraLateral({
  abierta,
  contraida,
  esEscritorio,
  alCerrar,
  alAlternarContraida,
  alNavegar,
}: Props) {
  const { perfil, salir } = useSesion();
  const ubicacion = useLocation();

  const { data: menu, isPending } = useQuery({
    queryKey: ['menu'],
    queryFn: () => api.get<Menu>('/menu'),

    staleTime: 5 * 60_000,
  });

  const grupos = useMemo(() => menu?.grupos ?? [], [menu]);

  const hrefs = useMemo(() => todosLosHrefs(menu), [menu]);

  const grupoActivo = useMemo(
    () =>
      grupos.find((g) => g.items.some((i) => rutaCoincide(ubicacion.pathname, i.href)))?.clave ??
      null,
    [grupos, ubicacion.pathname],
  );

  const [abierto, setAbierto] = useState<string | null>(() => {
    try {
      return sessionStorage.getItem(CLAVE_GRUPO_ABIERTO) || null;
    } catch {
      return null;
    }
  });

  const [grupoSincronizado, setGrupoSincronizado] = useState<string | null>(grupoActivo);
  if (grupoActivo && grupoActivo !== grupoSincronizado) {
    setGrupoSincronizado(grupoActivo);
    setAbierto(grupoActivo);
  }

  function recordarGrupo(clave: string | null) {
    try {
      sessionStorage.setItem(CLAVE_GRUPO_ABIERTO, clave ?? '');
    } catch {
    }
  }

  function alternarGrupo(clave: string) {
    setAbierto((previo) => {
      const siguiente = previo === clave ? null : clave;
      recordarGrupo(siguiente);
      return siguiente;
    });
  }

  function abrirDesdeElRiel(clave: string) {
    setAbierto(clave);
    recordarGrupo(clave);
    alAlternarContraida();
  }

  const enRiel = contraida && esEscritorio;

  const inicial = perfil?.nombre?.trim().charAt(0).toUpperCase() ?? 'U';

  return (
    <>

      {abierta && (
        <div
          className="fixed inset-0 z-40 bg-black/40 backdrop-blur-sm lg:hidden"
          onClick={alCerrar}
          aria-hidden
        />
      )}

      <aside
        className={[
          'fixed left-0 top-0 z-50 flex h-dvh flex-col bg-armazon-900 text-white shadow-2xl',
          'transition-[transform,width] duration-300 ease-in-out',

          'w-72',
          abierta ? 'translate-x-0' : '-translate-x-full',

          'lg:translate-x-0',
          contraida ? 'lg:w-20' : 'lg:w-72',
        ].join(' ')}

        aria-hidden={!abierta && !esEscritorio}
        inert={!abierta && !esEscritorio}
      >
        <div
          className={[
            'flex h-16 shrink-0 items-center border-b border-white/10',
            enRiel ? 'justify-center px-2' : 'justify-between px-5',
          ].join(' ')}
        >

          {!enRiel && (
            <div className="flex items-center gap-3">
              <Isotipo />
              <div>
                <p className="text-base font-bold leading-tight tracking-tight">SIREPAGAL</p>
                <p className="text-[10px] uppercase leading-tight tracking-wide text-armazon-400">
                  EPAGAL EP
                </p>
              </div>
            </div>
          )}

          <div className="flex items-center gap-1">

            <button
              type="button"
              onClick={alAlternarContraida}
              title={contraida ? 'Desplegar el menú' : 'Contraer el menú'}
              aria-label={contraida ? 'Desplegar el menú' : 'Contraer el menú'}
              aria-expanded={!contraida}
              className="hidden size-8 place-items-center rounded-lg text-armazon-400 transition-colors hover:bg-white/10 hover:text-white lg:grid"
            >
              {contraida ? (
                <PanelLeftOpen className="size-[18px]" aria-hidden />
              ) : (
                <PanelLeftClose className="size-[18px]" aria-hidden />
              )}
            </button>

            <button
              type="button"
              onClick={alCerrar}
              aria-label="Cerrar el menú"
              className="grid size-8 place-items-center rounded-lg text-armazon-400 transition-colors hover:bg-white/10 hover:text-white lg:hidden"
            >
              <X className="size-[18px]" aria-hidden />
            </button>
          </div>
        </div>

        <nav className={`flex-1 overflow-y-auto py-3 ${enRiel ? 'px-2' : 'px-3'}`}>
          {isPending && !enRiel && (
            <p className="px-3 py-2 text-xs text-armazon-500">Cargando menú…</p>
          )}

          <div className="space-y-0.5">
            {menu?.superiores.map((item) => (
              <Entrada
                key={item.id}
                item={item}
                enRiel={enRiel}
                alNavegar={alNavegar}
                hrefs={hrefs}
              />
            ))}
          </div>

          <div className="mt-3 space-y-1">
            {grupos.map((grupo) => (
              <Grupo
                key={grupo.id}
                grupo={grupo}
                enRiel={enRiel}
                expandido={!enRiel && abierto === grupo.clave}
                contieneActivo={grupo.clave === grupoActivo}
                alAlternar={() =>
                  enRiel ? abrirDesdeElRiel(grupo.clave) : alternarGrupo(grupo.clave)
                }
                alNavegar={alNavegar}
                hrefs={hrefs}
              />
            ))}
          </div>

          {menu && menu.inferiores.length > 0 && (
            <div className="mt-3 space-y-0.5 border-t border-white/10 pt-3">
              {menu.inferiores.map((item) => (
                <Entrada
                key={item.id}
                item={item}
                enRiel={enRiel}
                alNavegar={alNavegar}
                hrefs={hrefs}
              />
              ))}
            </div>
          )}
        </nav>

        <div className={`shrink-0 border-t border-white/10 py-3 ${enRiel ? 'px-2' : 'px-3'}`}>
          <div
            className={[
              'flex items-center py-2',
              enRiel ? 'justify-center px-0' : 'gap-3 px-3',
            ].join(' ')}
            title={enRiel ? `${perfil?.nombre} · ${perfil?.correo}` : undefined}
          >
            <span className="grid size-8 shrink-0 place-items-center rounded-lg bg-marca-500 text-sm font-bold text-white">
              {inicial}
            </span>
            {!enRiel && (
              <div className="min-w-0">
                <p className="truncate text-sm font-medium text-armazon-200">{perfil?.nombre}</p>
                <p className="truncate text-[11px] text-armazon-500">{perfil?.correo}</p>
              </div>
            )}
          </div>

          <button
            type="button"
            onClick={() => {
              alCerrar();
              void salir();
            }}
            title={enRiel ? 'Cerrar sesión' : undefined}
            aria-label="Cerrar sesión"
            className={[
              'mt-1 flex w-full items-center rounded-xl py-2.5 text-sm text-armazon-400',
              'transition-colors hover:bg-red-500/10 hover:text-red-400',
              enRiel ? 'justify-center px-0' : 'gap-3 px-3',
            ].join(' ')}
          >
            <LogOut className="size-[18px] shrink-0" aria-hidden />
            {!enRiel && <span>Cerrar sesión</span>}
          </button>
        </div>
      </aside>
    </>
  );
}

function Grupo({
  grupo,
  enRiel,
  expandido,
  contieneActivo,
  alAlternar,
  alNavegar,
  hrefs,
}: {
  grupo: GrupoDeMenu;
  enRiel: boolean;
  expandido: boolean;
  contieneActivo: boolean;
  alAlternar: () => void;
  alNavegar: () => void;
  hrefs: string[];
}) {
  return (
    <div>
      <button
        type="button"
        onClick={alAlternar}
        aria-expanded={expandido}

        title={enRiel ? grupo.titulo : undefined}
        className={[
          'relative flex w-full items-center rounded-xl py-2 text-sm font-semibold transition-colors',
          enRiel ? 'justify-center px-0' : 'gap-3 px-3',
          contieneActivo && !expandido
            ? 'text-marca-400 hover:bg-white/5'
            : 'text-armazon-200 hover:bg-white/5 hover:text-white',
          enRiel && contieneActivo ? 'bg-marca-500/15' : '',
        ].join(' ')}
      >
        <Icono nombre={grupo.icono} className="size-[18px] shrink-0" />

        {enRiel ? (
          <span className="sr-only">{grupo.titulo}</span>
        ) : (
          <>
            <span className="flex-1 text-left text-[12px] uppercase tracking-wide">
              {grupo.titulo}
            </span>

            {contieneActivo && !expandido && (
              <span className="size-1.5 shrink-0 rounded-full bg-marca-400" aria-hidden />
            )}
            <ChevronDown
              className={[
                'size-[15px] shrink-0 text-armazon-500 transition-transform duration-200',
                expandido ? 'rotate-180' : '',
              ].join(' ')}
              aria-hidden
            />
          </>
        )}
      </button>

      {!enRiel && (

        <div
          className={[
            'grid transition-[grid-template-rows] duration-200 ease-in-out',
            expandido ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]',
          ].join(' ')}
        >
          <div className="min-h-0 overflow-hidden">
            <div className="ml-4 space-y-0.5 border-l border-white/10 py-1 pl-2">
              {grupo.items.map((item) => (
                <Entrada
                  key={item.id}
                  item={item}
                  anidada
                  enRiel={false}
                  alNavegar={alNavegar}
                  hrefs={hrefs}
                />
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Entrada({
  item,
  anidada = false,
  enRiel,
  alNavegar,
  hrefs,
}: {
  item: ItemDeMenu;
  anidada?: boolean;
  enRiel: boolean;
  alNavegar: () => void;

  hrefs: string[];
}) {

  const tieneEntradaDebajo = hrefs.some((h) => h !== item.href && h.startsWith(`${item.href}/`));

  return (
    <NavLink
      to={item.href}
      onClick={alNavegar}
      end={item.href === '/inicio' || tieneEntradaDebajo}
      title={enRiel ? item.etiqueta : undefined}
      className={({ isActive }) =>
        [
          'flex items-center rounded-xl py-2 text-sm font-medium transition-colors',
          enRiel ? 'justify-center px-0' : 'gap-3',
          enRiel ? '' : anidada ? 'pl-4 pr-3 text-[13px]' : 'px-3',
          isActive
            ? 'bg-marca-500/15 text-marca-400'
            : 'text-armazon-300 hover:bg-white/5 hover:text-white',
        ].join(' ')
      }
    >
      <Icono
        nombre={item.icono}
        className={anidada && !enRiel ? 'size-4 shrink-0' : 'size-[18px] shrink-0'}
      />
      {enRiel ? (
        <span className="sr-only">{item.etiqueta}</span>
      ) : (
        <span className="truncate">{item.etiqueta}</span>
      )}
    </NavLink>
  );
}

function Isotipo() {
  return (
    <span className="grid size-9 shrink-0 place-items-center rounded-xl bg-white p-1">
      <img src="/isotipo-epagal.png" alt="" width={28} height={28} className="size-7" />
    </span>
  );
}

function todosLosHrefs(menu: Menu | undefined): string[] {
  if (!menu) {
    return [];
  }
  return [
    ...menu.superiores.map((i) => i.href),
    ...menu.grupos.flatMap((g) => g.items.map((i) => i.href)),
    ...menu.inferiores.map((i) => i.href),
  ];
}

function rutaCoincide(actual: string, href: string): boolean {
  return actual === href || actual.startsWith(`${href}/`);
}
